import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToId } from "../lib/scroll";

const HERO_IMG = "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=80&w=2000";
const LINES = [["Big Music.", "text-white"], ["Summer Nights.", "text-gradient"], ["Fallsview.", "text-white"]];
const ease = [0.16, 1, 0.3, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [3, -3]), { stiffness: 60, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-4, 4]), { stiffness: 60, damping: 20 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section ref={ref} onMouseMove={onMove} data-testid="hero-section" className="relative flex min-h-[100svh] items-end overflow-hidden" style={{ perspective: 1200 }}>
      <motion.div style={{ y: imgY, rotateX: rx, rotateY: ry, scale: 1.12 }} className="absolute inset-0">
        <motion.img
          src={HERO_IMG}
          alt="Crowd facing a glowing outdoor concert stage at night"
          initial={{ scale: 1.25, filter: "brightness(0.3)" }}
          animate={{ scale: 1, filter: "brightness(1)" }}
          transition={{ duration: 2.4, ease }}
          className="h-full w-full object-cover"
          fetchPriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B0C10]/50 via-[#0B0C10]/40 to-[#0B0C10]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_20%,rgba(157,78,221,0.35),transparent_55%)]" />
      <div className="grain absolute inset-0" />

      <motion.div style={{ y: textY }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-16 pt-32 lg:px-12 lg:pb-24">
        <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.8 }} className="mb-6 inline-flex items-center gap-3 rounded-full border border-white/15 bg-black/30 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-200 backdrop-blur-md">
          <span className="h-2 w-2 animate-pulse rounded-full bg-sunset" /> Niagara Falls · Ontario · Canada
        </motion.p>
        <h1 data-testid="hero-headline" className="font-display text-[15vw] font-extrabold uppercase leading-[0.86] tracking-tight sm:text-[11vw] lg:text-[10rem] xl:text-[11rem]">
          {LINES.map(([line, color], i) => (
            <span key={line} className="block overflow-hidden whitespace-nowrap pb-[0.04em]">
              <motion.span className={`block ${color}`} initial={{ y: "105%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, delay: 0.35 + i * 0.14, ease }}>
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
        <div className="mt-8 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.9, ease }} className="max-w-md text-base text-zinc-200 md:text-lg">
            Experience a summer of live outdoor concerts in the heart of Niagara Falls, Canada.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.9, ease }} className="flex flex-col gap-3 sm:flex-row">
            <button data-testid="hero-view-lineup-btn" onClick={() => scrollToId("lineup")} className="btn-ghost btn-lg justify-center">View the Lineup</button>
            <button data-testid="hero-get-tickets-btn" onClick={() => scrollToId("next-show")} className="btn-primary btn-lg justify-center">Get Tickets</button>
          </motion.div>
        </div>
      </motion.div>
      <button aria-label="Scroll to next concert" data-testid="hero-scroll-cue" onClick={() => scrollToId("next-show")} className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 text-zinc-300 lg:block">
        <ArrowDown className="animate-bounce" size={20} />
      </button>
    </section>
  );
};
